import {
  Activity,
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  CirclePause,
  CirclePlay,
  Clock,
  ClipboardList,
  Copy,
  DoorClosed,
  DoorOpen,
  ExternalLink,
  Flag,
  Gauge,
  History,
  KeyRound,
  ListChecks,
  LogOut,
  MonitorUp,
  Play,
  Shield,
  Sparkles,
  Target,
  Trash2,
  Trophy,
  Users,
  Wifi,
  WifiOff,
  X,
  Zap,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { BrandLockup } from '../components/brand/DatabinexMark';
import { TeamAvatar } from '../components/team/Avatar';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { callFunction, errorMessage } from '../lib/callables';
import { cn } from '../lib/cn';
import { useAuth } from '../providers/AuthProvider';
import type { AnswerRecord, ArenaEvent, ContestArchive, TeamCode, TeamPublic, TeamState } from '../types/arena';

const statusLabel = { registration: 'ثبت‌نام', countdown: 'شمارش معکوس', running: 'در حال اجرا', paused: 'متوقف', finished: 'پایان‌یافته' } as const;
const statusTone = {
  registration: 'bg-sky-50 text-sky-700 ring-sky-100',
  countdown: 'bg-amber-50 text-amber-700 ring-amber-100',
  running: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  paused: 'bg-orange-50 text-orange-700 ring-orange-100',
  finished: 'bg-slate-100 text-slate-700 ring-slate-200',
} as const;

const flowSteps = [
  { title: 'کد بساز', detail: 'برای هر تیم یک کد تازه بده.' },
  { title: 'تیم‌ها وارد شوند', detail: 'نام و اعضا را همین‌جا چک کن.' },
  { title: 'در ورود را ببند', detail: 'قبل از شروع، تیم تازه اضافه نشود.' },
  { title: 'شروع / مکث', detail: 'تابلو و اتصال تیم‌ها را زیر نظر بگیر.' },
  { title: 'پایان مسابقه', detail: 'نتیجه نهایی شود تا تیم‌ها صفحه نتیجه را ببینند.' },
  { title: 'آرشیو جداگانه', detail: 'وقتی کار تمام شد، log را ذخیره و پنل را خالی کن.' },
] as const;

export function AdminPage({
  event,
  teams,
  states,
  codes,
  answers,
  archives,
}: {
  event: ArenaEvent;
  teams: TeamPublic[];
  states: Record<string, TeamState>;
  codes: TeamCode[];
  answers: Record<string, Record<string, AnswerRecord>>;
  archives: ContestArchive[];
}) {
  const { logout } = useAuth();
  const [busyAction, setBusyAction] = useState('');
  const [notice, setNotice] = useState<{ type: 'ok' | 'error'; text: string } | null>(null);
  const [selectedTeam, setSelectedTeam] = useState('');
  const [delta, setDelta] = useState('');
  const [reason, setReason] = useState('');
  const [codeLabel, setCodeLabel] = useState('');
  const [newCode, setNewCode] = useState<{ code: string; teamId: string } | null>(null);
  const [copiedCode, setCopiedCode] = useState('');
  const [activeView, setActiveView] = useState<'control' | 'analytics'>('control');
  const noticeTimer = useRef<number | null>(null);

  const onlineCount = useMemo(
    () =>
      teams.filter((team) => {
        const heartbeat = states[team.id]?.lastHeartbeatMs ?? 0;
        return Date.now() - heartbeat <= 10_000;
      }).length,
    [states, teams],
  );
  const totalCorrect = teams.reduce((sum, team) => sum + team.correctCount, 0);
  const leader = teams[0] ?? null;
  const activeCodes = codes.filter((code) => code.status === 'active').length;

  useEffect(() => () => {
    if (noticeTimer.current) window.clearTimeout(noticeTimer.current);
  }, []);

  const clearNotice = () => {
    if (noticeTimer.current) {
      window.clearTimeout(noticeTimer.current);
      noticeTimer.current = null;
    }
    setNotice(null);
  };

  const showNotice = (nextNotice: { type: 'ok' | 'error'; text: string }) => {
    if (noticeTimer.current) window.clearTimeout(noticeTimer.current);
    setNotice(nextNotice);
    if (nextNotice.type === 'ok') {
      noticeTimer.current = window.setTimeout(() => {
        setNotice((current) => (current === nextNotice ? null : current));
        noticeTimer.current = null;
      }, 4500);
    }
  };

  const action = async (name: string, data: Record<string, unknown>) => {
    setBusyAction(name);
    clearNotice();
    try {
      const result = await callFunction<Record<string, unknown>, { archivedContestId?: string }>(name, data);
      showNotice({
        type: 'ok',
        text:
          data.action === 'new-contest'
            ? result.archivedContestId
              ? 'مسابقه قبلی آرشیو شد و ورود مسابقه جدید فعال است.'
              : 'مسابقه جدید آماده شد و ورود تیم‌ها فعال است.'
            : result.archivedContestId
              ? 'مسابقه آرشیو شد و پنل برای دوره بعدی آماده است.'
              : data.action === 'finish'
                ? 'مسابقه پایان یافت؛ تیم‌ها حالا صفحه نتیجه را می‌بینند.'
                : 'انجام شد؛ روی تابلو اعمال شد.',
      });
    } catch (error) {
      showNotice({ type: 'error', text: errorMessage(error) });
    } finally {
      setBusyAction('');
    }
  };

  const control = (controlAction: string) => action('adminControl', { action: controlAction });
  const createTeamCode = async () => {
    setBusyAction('adminCreateTeamCode');
    clearNotice();
    setNewCode(null);
    try {
      const result = await callFunction<{ label: string }, { code: string; teamId: string }>('adminCreateTeamCode', { label: codeLabel.trim() });
      setNewCode({ code: result.code, teamId: result.teamId });
      setCodeLabel('');
      showNotice({ type: 'ok', text: 'کد آماده شد؛ بدهید دست تیم تا وارد میدان شود.' });
    } catch (error) {
      showNotice({ type: 'error', text: errorMessage(error) });
    } finally {
      setBusyAction('');
    }
  };
  const codeAction = (codeHash: string, nextStatus: TeamCode['status']) => action('adminTeamCodeAction', { codeHash, action: nextStatus === 'active' ? 'activate' : 'revoke' });
  const copyTeamCode = async (code: string) => {
    await navigator.clipboard.writeText(code);
    setCopiedCode(code);
    window.setTimeout(() => setCopiedCode((current) => (current === code ? '' : current)), 1600);
  };
  const adjustScore = async () => {
    if (!selectedTeam || !delta || reason.trim().length < 5) return;
    await action('adminAdjustScore', { teamId: selectedTeam, delta: Number(delta), reason: reason.trim() });
    setDelta('');
    setReason('');
  };

  return (
    <main className="min-h-screen bg-[#F4F6F8] text-ink">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 px-6 backdrop-blur-xl">
        <div className="mx-auto flex h-[74px] max-w-[1540px] items-center">
          <BrandLockup />
          <div className="mx-6 hidden h-7 w-px bg-slate-200 md:block" />
          <div className="hidden items-center gap-2 text-xs font-black text-slate-500 md:flex">
            <Shield className="size-4 text-brand" /> اتاق فرمان مسابقه
          </div>
          <div className="mr-auto flex items-center gap-3">
            <span className={cn('hidden rounded-full px-3 py-1 text-[10px] font-black ring-1 md:inline-flex', statusTone[event.status])}>{statusLabel[event.status]}</span>
            <Link to="/leaderboard" target="_blank">
              <Button variant="dark" size="sm">
                <MonitorUp className="size-4" /> تابلوی سالن <ExternalLink className="size-3" />
              </Button>
            </Link>
            <Button variant="ghost" size="icon" onClick={() => void logout()} aria-label="خروج">
              <LogOut className="size-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1540px] px-6 py-6">
        <section className="overflow-hidden rounded-[28px] bg-[#0D1422] text-white shadow-[0_24px_70px_rgba(15,23,42,.18)]">
          <div className="grid gap-0 xl:grid-cols-[1.05fr_.95fr]">
            <div className="relative p-7">
              <div className="absolute inset-0 opacity-35 [background:radial-gradient(circle_at_15%_15%,rgba(242,106,33,.28),transparent_34%),radial-gradient(circle_at_80%_10%,rgba(37,99,235,.20),transparent_36%)]" />
              <div className="relative">
                <span className="inline-flex items-center gap-2 rounded-full bg-white/8 px-3 py-1 text-[10px] font-black text-orange-100 ring-1 ring-white/10">
                  <Sparkles className="size-3.5 text-brand" /> مسابقه جاری
                </span>
                <h1 className="mt-5 text-3xl font-black tracking-tight md:text-4xl">DataBinex T-SQL Arena</h1>
                <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-300">
                  اول کد بساز، تیم‌ها را وارد کن، بازی را شروع کن؛ آخر کار اول «پایان مسابقه» را بزن تا نتیجه برای تیم‌ها نمایش داده شود، بعد اگر خواستی آرشیو کن و مسابقه بعدی را از صفر بساز.
                </p>
                <div className="mt-7 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  <HeroMetric label="وضعیت" value={statusLabel[event.status]} />
                  <HeroMetric label="تیم‌ها" value={teams.length.toLocaleString('fa-IR')} />
                  <HeroMetric label="کد فعال" value={activeCodes.toLocaleString('fa-IR')} />
                  <HeroMetric label="رهبر فعلی" value={leader?.name ?? '—'} />
                </div>
              </div>
            </div>
            <div className="border-t border-white/10 bg-white/[.035] p-5 xl:border-r xl:border-t-0">
              <span className="mb-4 flex items-center gap-2 text-[10px] font-black text-white/45">
                <ClipboardList className="size-4 text-brand" /> راهنمای سریع مدیر
              </span>
              <div className="grid gap-2">
                {flowSteps.map((step, index) => (
                  <div key={step.title} className="flex gap-3 rounded-2xl bg-white/[.045] p-3 ring-1 ring-white/8">
                    <span className={cn('grid size-8 shrink-0 place-items-center rounded-xl text-xs font-black', index <= activeFlowStep(event) ? 'bg-brand text-white' : 'bg-white/8 text-white/40')}>
                      {(index + 1).toLocaleString('fa-IR')}
                    </span>
                    <div>
                      <b className="block text-xs">{step.title}</b>
                      <span className="mt-1 block text-[10px] leading-5 text-white/45">{step.detail}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-[22px] border border-slate-200 bg-white p-2 shadow-sm">
          <div className="flex gap-2">
            <button type="button" onClick={() => setActiveView('control')} className={cn('inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-xs font-black transition', activeView === 'control' ? 'bg-ink text-white shadow-sm' : 'text-slate-500 hover:bg-slate-50 hover:text-ink')}>
              <Shield className="size-4" /> اتاق فرمان
            </button>
            <button type="button" onClick={() => setActiveView('analytics')} className={cn('inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-xs font-black transition', activeView === 'analytics' ? 'bg-ink text-white shadow-sm' : 'text-slate-500 hover:bg-slate-50 hover:text-ink')}>
              <BarChart3 className="size-4" /> تحلیل مسابقه
            </button>
          </div>
          <span className="px-3 text-[11px] font-bold text-slate-400">نمای مدیر برای کنترل زنده و ارائه داده‌های مسابقه</span>
        </section>

        {activeView === 'control' ? (
          <>
            <section className="mt-6 grid gap-4 border-y border-slate-200 py-5 md:grid-cols-4">
              <Metric icon={<Gauge />} label="وضعیت مسابقه" value={statusLabel[event.status]} accent />
              <Metric icon={<Users />} label="تیم‌های ثبت‌شده" value={teams.length.toLocaleString('fa-IR')} />
              <Metric icon={<Wifi />} label="تیم‌های آنلاین" value={`${onlineCount.toLocaleString('fa-IR')} / ${teams.length.toLocaleString('fa-IR')}`} />
              <Metric icon={<Activity />} label="Queryهای کامل" value={totalCorrect.toLocaleString('fa-IR')} />
            </section>

            <section className="space-y-6 py-6">
          <ControlPanel event={event} busyAction={busyAction} teams={teams} control={control} />

          <div className="grid gap-6 2xl:grid-cols-[minmax(0,1fr)_440px]">
            <div className="min-w-0">
              <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
                <div>
                  <span className="text-[10px] font-black text-brand">وضعیت زنده</span>
                  <h2 className="mt-1 text-2xl font-black">تیم‌ها و سؤال فعال</h2>
                </div>
                <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-slate-400 shadow-sm">هر ثانیه تازه می‌شود</span>
              </div>
              <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm">
                <div className="overflow-x-auto">
                  <div className="grid min-w-[860px] grid-cols-[minmax(240px,1.2fr)_100px_150px_120px_90px_110px] border-b border-slate-200 bg-slate-50 px-4 py-3 text-[10px] font-bold text-slate-400">
                    <span>تیم</span><span>اتصال</span><span>سؤال فعلی</span><span>امتیاز ترکیبی</span><span>زنجیره</span><span>عملیات</span>
                  </div>
                  {teams.map((team) => {
                    const state = states[team.id];
                    const online = Date.now() - (state?.lastHeartbeatMs ?? 0) <= 10_000;
                    return (
                      <div key={team.id} className="grid min-h-[78px] min-w-[860px] grid-cols-[minmax(240px,1.2fr)_100px_150px_120px_90px_110px] items-center border-b border-slate-100 px-4 last:border-0 hover:bg-orange-50/30">
                        <div className="flex min-w-0 items-center gap-3">
                          <TeamAvatar avatarId={team.avatarId} color={team.color} className="size-10 rounded-xl" />
                          <div className="min-w-0">
                            <b className="block truncate text-sm">{team.name}</b>
                            <span className="block truncate text-[10px] text-slate-400">{team.members.join('، ')}</span>
                          </div>
                        </div>
                        <span className={cn('inline-flex w-fit items-center gap-2 rounded-full px-2 py-1 text-[10px] font-black', online ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700')}>
                          {online ? <Wifi className="size-3.5" /> : <WifiOff className="size-3.5" />}{online ? 'متصل' : 'قطع'}
                        </span>
                        <span className="font-mono text-[10px] text-slate-500" dir="ltr">
                          {state?.currentQuestionId ?? '—'}
                          <small className="mt-1 block text-slate-300">{state ? `${toFaNumber(state.currentIndex + 1)} از ${toFaNumber(12)}` : ''}</small>
                        </span>
                        <b className="font-mono text-base">{team.score.toLocaleString('fa-IR')}</b>
                        <b className="font-mono text-sm text-brand">×{team.streak.toLocaleString('fa-IR')}</b>
                        <div className="flex gap-1.5">
                          <Button variant="ghost" size="icon" title="حذف تیم" disabled={busyAction !== ''} onClick={() => void action('adminTeamAction', { teamId: team.id, action: 'remove' })}>
                            <Trash2 className="size-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {!teams.length && <div className="grid h-44 place-items-center text-sm text-slate-400">هنوز تیمی برای این مسابقه ثبت نشده است.</div>}
              </div>
            </div>

            <TeamCodesPanel
              codes={codes}
              teams={teams}
              busyAction={busyAction}
              codeLabel={codeLabel}
              setCodeLabel={setCodeLabel}
              newCode={newCode}
              copiedCode={copiedCode}
              createTeamCode={createTeamCode}
              codeAction={codeAction}
              copyTeamCode={copyTeamCode}
            />
          </div>

          <div className="grid gap-6 xl:grid-cols-2">
            <ScorePanel
              teams={teams}
              selectedTeam={selectedTeam}
              setSelectedTeam={setSelectedTeam}
              delta={delta}
              setDelta={setDelta}
              reason={reason}
              setReason={setReason}
              busyAction={busyAction}
              adjustScore={adjustScore}
            />
            <ArchivePanel archives={archives} />
          </div>
            </section>
          </>
        ) : (
          <AdminAnalytics event={event} teams={teams} states={states} answers={answers} archives={archives} />
        )}
      </div>
      {notice?.type === 'ok' && <AdminNoticeToast text={notice.text} />}
      {notice?.type === 'error' && <AdminErrorModal text={notice.text} onClose={clearNotice} />}
    </main>
  );
}

function ControlPanel({ event, busyAction, teams, control }: { event: ArenaEvent; busyAction: string; teams: TeamPublic[]; control: (action: string) => Promise<void> | void }) {
  const canPrepareNewContest = event.status === 'finished' || (event.status === 'registration' && !event.admissionOpen && teams.length === 0);
  const admissionLockedByStatus = event.status !== 'registration';

  return (
    <div className="overflow-hidden rounded-[24px] bg-ink p-5 text-white shadow-lift">
      <div className="grid gap-5 xl:grid-cols-[minmax(260px,.75fr)_minmax(0,1.25fr)]">
        <div>
          <span className="text-[10px] font-black text-brand">کنترل بازی</span>
          <h2 className="mt-1 text-xl font-black">فرمان‌های اصلی مسابقه</h2>
          <p className="mt-2 max-w-xl text-[11px] leading-6 text-white/45">ورود تیم‌ها، شروع، مکث، پایان رسمی و آماده‌سازی مسابقه بعدی از همین نوار کنترل می‌شود.</p>
        </div>
        <div className="grid gap-3 lg:grid-cols-[minmax(240px,.85fr)_minmax(0,1.15fr)]">
          <div className="rounded-2xl border border-white/10 bg-white/[.045] p-2">
            <div className="mb-2 flex items-center justify-between px-1 text-[10px] font-black text-white/45">
              <span>وضعیت ورود تیم‌ها</span>
              <span className={cn('rounded-full px-2 py-0.5', event.admissionOpen ? 'bg-emerald-400/15 text-emerald-200' : 'bg-red-400/15 text-red-200')}>
                {event.admissionOpen ? 'فعال است' : 'غیرفعال است'}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button
                size="sm"
                variant="secondary"
                className={cn(event.admissionOpen && 'bg-emerald-500 text-white ring-emerald-300/40 hover:bg-emerald-500')}
                disabled={busyAction !== '' || event.status !== 'registration'}
                onClick={() => !event.admissionOpen && void control('open-admission')}
              >
                <DoorOpen className="size-4" /> فعال
              </Button>
              <Button
                size="sm"
                variant="secondary"
                className={cn(!event.admissionOpen && 'bg-red-500 text-white ring-red-300/40 hover:bg-red-500')}
                disabled={busyAction !== '' || event.status !== 'registration'}
                onClick={() => event.admissionOpen && void control('close-admission')}
              >
                <DoorClosed className="size-4" /> غیرفعال
              </Button>
            </div>
            {admissionLockedByStatus && (
              <p className="mt-2 rounded-xl bg-white/[.055] px-3 py-2 text-[10px] leading-5 text-white/45">
                تغییر فعال/غیرفعال فقط قبل از شروع مجاز است. اگر مسابقه قبلی تمام شده، از دکمه مسابقه جدید استفاده کن.
              </p>
            )}
          </div>
          <div className="grid gap-2 sm:grid-cols-3">
            <Button size="sm" variant="secondary" disabled={busyAction !== '' || event.status !== 'registration' || teams.length < 1} onClick={() => void control('start')}>
              <Play className="size-4 fill-current" /> شروع بازی
            </Button>
            <Button size="sm" variant="secondary" disabled={busyAction !== '' || !['running', 'countdown'].includes(event.status)} onClick={() => void control('pause')}>
              <CirclePause className="size-4" /> مکث
            </Button>
            <Button size="sm" variant="secondary" disabled={busyAction !== '' || event.status !== 'paused'} onClick={() => void control('resume')}>
              <CirclePlay className="size-4" /> ادامه
            </Button>
            <Button className="sm:col-span-1" size="sm" variant="danger" disabled={busyAction !== '' || event.status === 'registration' || event.status === 'finished'} onClick={() => void control('finish')}>
              <Flag className="size-4" /> پایان رسمی
            </Button>
            <Button className="sm:col-span-2" size="sm" variant="dark" disabled={busyAction !== '' || !canPrepareNewContest} onClick={() => void control('new-contest')}>
              <History className="size-4" /> آرشیو و بازکردن مسابقه جدید
            </Button>
          </div>
        </div>
      </div>
      {event.pausedReason === 'connection' && (
        <div className="mt-4 rounded-xl border border-red-400/20 bg-red-400/8 p-3 text-xs leading-6 text-red-200">
          اتصال یک تیم قطع شده است؛ بازی تا برگشت تیم مکث می‌ماند.
        </div>
      )}
    </div>
  );
}

function AdminAnalytics({
  event,
  teams,
  states,
  answers,
  archives,
}: {
  event: ArenaEvent;
  teams: TeamPublic[];
  states: Record<string, TeamState>;
  answers: Record<string, Record<string, AnswerRecord>>;
  archives: ContestArchive[];
}) {
  const analytics = useMemo(() => buildAnalytics(teams, states, answers, archives), [answers, archives, states, teams]);
  const hasData = analytics.totalAnswers > 0;

  return (
    <section className="space-y-6 py-6">
      <div className="overflow-hidden rounded-[30px] bg-[#0D1422] text-white shadow-[0_24px_70px_rgba(15,23,42,.18)]">
        <div className="relative p-7">
          <div className="absolute inset-0 opacity-40 [background:radial-gradient(circle_at_12%_15%,rgba(242,106,33,.32),transparent_30%),radial-gradient(circle_at_88%_5%,rgba(124,58,237,.24),transparent_34%),linear-gradient(135deg,rgba(255,255,255,.06),transparent_42%)]" />
          <div className="relative">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/8 px-3 py-1 text-[10px] font-black text-orange-100 ring-1 ring-white/10">
              <BarChart3 className="size-3.5 text-brand" /> تحلیل مسابقه
            </span>
            <div className="mt-5 flex flex-wrap items-end justify-between gap-4">
              <div>
                <h2 className="text-3xl font-black tracking-tight">گزارش عملکرد T-SQL Arena</h2>
                <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-300">
                  زمان پاسخ، دقت Query و سرعت تیم‌ها برای گزارش بعد از مسابقه یا ارائه زنده آماده شده است.
                </p>
              </div>
              <span className="rounded-full border border-white/10 bg-white/8 px-4 py-2 text-[11px] font-black text-white/55">
                {analytics.sourceLabel}
              </span>
            </div>
            <div className="mt-7 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <AnalyticsStat icon={<Target />} label="نرخ پاسخ درست" value={analytics.correctRateLabel} />
              <AnalyticsStat icon={<Clock />} label="میانگین زمان پاسخ" value={analytics.averageAnswerLabel} />
              <AnalyticsStat icon={<Zap />} label="سریع‌ترین پاسخ" value={analytics.fastestAnswerLabel} />
              <AnalyticsStat icon={<Activity />} label="پاسخ‌های ثبت‌شده" value={analytics.totalAnswers.toLocaleString('fa-IR')} />
            </div>
          </div>
        </div>
      </div>

      {!hasData ? (
        <div className="grid min-h-72 place-items-center rounded-[28px] border border-dashed border-slate-300 bg-white p-8 text-center shadow-sm">
          <div>
            <BarChart3 className="mx-auto size-10 text-slate-300" />
            <h3 className="mt-4 text-xl font-black text-ink">هنوز داده تحلیلی نداریم</h3>
            <p className="mt-2 max-w-lg text-sm leading-7 text-slate-500">
              بعد از ثبت اولین Query، نمودار میانگین زمان، دقت سؤال‌ها و سرعت تیم‌ها اینجا پر می‌شود.
            </p>
            <small className="mt-4 block text-xs text-slate-400">وضعیت فعلی: {statusLabel[event.status]}</small>
          </div>
        </div>
      ) : (
        <>
          <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_420px]">
            <AnalyticsPanel title="میانگین زمان پاسخ به هر سؤال" subtitle="هر ردیف میانگین زمان پاسخ‌های ثبت‌شده برای یک سؤال را نشان می‌دهد.">
              <div className="space-y-4">
                {analytics.questionRows.slice(0, 10).map((row) => (
                  <BarRow
                    key={row.questionId}
                    label={row.questionId}
                    value={formatDuration(row.averageMs)}
                    detail={`${row.count.toLocaleString('fa-IR')} پاسخ · دقت ${formatPercent(row.correctRate)}`}
                    percent={row.barPercent}
                  />
                ))}
              </div>
            </AnalyticsPanel>

            <AnalyticsPanel title="سریع‌ترین پاسخ هر سطح" subtitle="بهترین پاسخ درست در هر دسته سختی.">
              <div className="space-y-3">
                {analytics.fastestByDifficulty.map((row) => (
                  <div key={row.difficulty} className="rounded-2xl bg-slate-50 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-xs font-black text-slate-500">{difficultyLabel(row.difficulty)}</span>
                      <b className="font-mono text-lg text-ink">{row.answerMs ? formatDuration(row.answerMs) : '—'}</b>
                    </div>
                    <p className="mt-2 truncate text-xs text-slate-400">{row.teamName ? `${row.teamName} · ${row.questionId}` : 'هنوز پاسخ درست ثبت نشده است'}</p>
                  </div>
                ))}
              </div>
            </AnalyticsPanel>
          </div>

          <div className="grid gap-6 xl:grid-cols-2">
            <AnalyticsPanel title="عملکرد تیم‌ها" subtitle="سرعت، دقت و امتیاز ترکیبی هر تیم برای مقایسه سریع.">
              <div className="space-y-3">
                {analytics.teamRows.map((row, index) => (
                  <div key={row.teamId} className="grid grid-cols-[32px_minmax(0,1fr)_90px] items-center gap-3 rounded-2xl border border-slate-100 bg-white p-3">
                    <span className="grid size-8 place-items-center rounded-xl bg-slate-100 text-xs font-black text-slate-500">{toFaNumber(index + 1)}</span>
                    <div className="min-w-0">
                      <b className="block truncate text-sm">{row.teamName}</b>
                      <span className="text-[10px] text-slate-400">{row.correct.toLocaleString('fa-IR')} درست از {row.count.toLocaleString('fa-IR')} پاسخ · میانگین {formatDuration(row.averageMs)}</span>
                    </div>
                    <strong className="font-mono text-sm text-brand">{row.score.toLocaleString('fa-IR')}</strong>
                  </div>
                ))}
              </div>
            </AnalyticsPanel>

            <AnalyticsPanel title="دقت بر اساس سختی سؤال" subtitle="برای دیدن اینکه مسابقه کجا سخت‌تر یا سریع‌تر شده است.">
              <div className="space-y-5">
                {analytics.difficultyRows.map((row) => (
                  <BarRow
                    key={row.difficulty}
                    label={difficultyLabel(row.difficulty)}
                    value={formatPercent(row.correctRate)}
                    detail={`${row.count.toLocaleString('fa-IR')} پاسخ · میانگین ${formatDuration(row.averageMs)}`}
                    percent={Math.max(6, row.correctRate * 100)}
                    tone={difficultyTone(row.difficulty)}
                  />
                ))}
              </div>
            </AnalyticsPanel>
          </div>
        </>
      )}
    </section>
  );
}

function AdminNoticeToast({ text }: { text: string }) {
  return (
    <div className="fixed left-1/2 top-24 z-40 w-[min(440px,calc(100vw-32px))] -translate-x-1/2 rounded-2xl border border-emerald-200 bg-white p-4 text-sm leading-7 text-emerald-800 shadow-[0_18px_60px_rgba(15,23,42,.18)]">
      <div className="flex items-start gap-3">
        <CheckCircle2 className="mt-1 size-5 shrink-0 text-emerald-600" />
        <span>{text}</span>
      </div>
    </div>
  );
}

function AdminErrorModal({ text, onClose }: { text: string; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/60 px-4 backdrop-blur-sm" role="alertdialog" aria-modal="true" aria-labelledby="admin-error-title">
      <div className="w-full max-w-md overflow-hidden rounded-[28px] bg-white text-ink shadow-[0_28px_90px_rgba(15,23,42,.35)]">
        <div className="bg-red-50 px-5 py-4 text-red-700">
          <div className="flex items-center justify-between gap-4">
            <span className="inline-flex items-center gap-2 text-xs font-black">
              <AlertTriangle className="size-5" /> خطای پنل مدیر
            </span>
            <button type="button" className="grid size-9 place-items-center rounded-full bg-white text-red-600 shadow-sm transition hover:bg-red-100" onClick={onClose} aria-label="بستن خطا">
              <X className="size-4" />
            </button>
          </div>
        </div>
        <div className="p-6">
          <h2 id="admin-error-title" className="text-xl font-black">عملیات انجام نشد</h2>
          <p className="mt-3 rounded-2xl bg-slate-50 p-4 text-sm leading-7 text-slate-700">{text}</p>
          <p className="mt-4 text-xs leading-6 text-slate-400">
            اگر وضعیت روی پایان‌یافته یا ورود غیرفعال گیر کرده، اول «آرشیو و بازکردن مسابقه جدید» را بزن؛ اگر وسط بازی هستید، ابتدا پایان رسمی مسابقه را ثبت کنید.
          </p>
          <Button className="mt-6 w-full" variant="dark" onClick={onClose}>
            متوجه شدم
          </Button>
        </div>
      </div>
    </div>
  );
}

type DifficultyBucket = 'easy' | 'medium' | 'hard';

type FlatAnswer = AnswerRecord & {
  teamId: string;
  teamName: string;
  questionId: string;
  difficulty: DifficultyBucket;
};

function AnalyticsStat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[.055] p-4">
      <span className="grid size-10 place-items-center rounded-xl bg-white/8 text-brand">{icon}</span>
      <span className="mt-4 block text-[10px] font-black text-white/35">{label}</span>
      <strong className="mt-1 block truncate text-xl font-black">{value}</strong>
    </div>
  );
}

function AnalyticsPanel({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  return (
    <div className="rounded-[26px] border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-5">
        <span className="text-[10px] font-black text-brand">گزارش تحلیلی</span>
        <h3 className="mt-1 text-lg font-black text-ink">{title}</h3>
        <p className="mt-2 text-xs leading-6 text-slate-400">{subtitle}</p>
      </div>
      {children}
    </div>
  );
}

function BarRow({ label, value, detail, percent, tone = 'bg-brand' }: { label: string; value: string; detail: string; percent: number; tone?: string }) {
  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <b className="block truncate text-sm text-ink" dir="ltr">{label}</b>
          <span className="mt-1 block text-[10px] text-slate-400">{detail}</span>
        </div>
        <strong className="shrink-0 font-mono text-sm text-slate-700">{value}</strong>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-slate-100">
        <div className={cn('h-full rounded-full', tone)} style={{ width: `${Math.min(100, Math.max(4, percent))}%` }} />
      </div>
    </div>
  );
}

function buildAnalytics(teams: TeamPublic[], states: Record<string, TeamState>, answers: Record<string, Record<string, AnswerRecord>>, archives: ContestArchive[]) {
  const currentAnswers = flattenAnswers(answers, teams);
  const latestArchive = archives[0] ?? null;
  const archivedAnswers = latestArchive ? flattenAnswers(latestArchive.answers, latestArchive.teams) : [];
  const usingArchive = currentAnswers.length === 0 && archivedAnswers.length > 0;
  const sourceAnswers = usingArchive ? archivedAnswers : currentAnswers;
  const sourceTeams = usingArchive ? latestArchive?.teams ?? [] : teams;
  const activeQuestionCount = Object.values(states).filter((state) => state.currentQuestionId).length;
  const processedAnswers = sourceAnswers.filter((answer) => answer.status === 'finalized' || answer.status === 'timeout');
  const timedAnswers = processedAnswers.filter((answer) => typeof answer.answerMs === 'number' && answer.answerMs > 0);
  const correctAnswers = processedAnswers.filter((answer) => answer.correct);
  const fastestAnswer = timedAnswers.filter((answer) => answer.correct).sort((a, b) => Number(a.answerMs) - Number(b.answerMs))[0] ?? null;
  const averageAnswerMs = average(timedAnswers.map((answer) => Number(answer.answerMs)));
  const correctRate = processedAnswers.length ? correctAnswers.length / processedAnswers.length : 0;
  const questionRows = buildQuestionRows(processedAnswers);
  const teamRows = buildTeamRows(processedAnswers, sourceTeams);
  const difficultyRows = buildDifficultyRows(processedAnswers);
  const fastestByDifficulty = (['easy', 'medium', 'hard'] as const).map((difficulty) => {
    const fastest = timedAnswers
      .filter((answer) => answer.correct && answer.difficulty === difficulty)
      .sort((a, b) => Number(a.answerMs) - Number(b.answerMs))[0];
    return {
      difficulty,
      answerMs: fastest?.answerMs ?? null,
      teamName: fastest?.teamName ?? '',
      questionId: fastest?.questionId ?? '',
    };
  });

  return {
    totalAnswers: processedAnswers.length,
    sourceLabel: usingArchive ? 'آخرین آرشیو مسابقه' : activeQuestionCount > 0 ? `داده زنده · ${activeQuestionCount.toLocaleString('fa-IR')} سؤال فعال` : 'داده زنده مسابقه',
    correctRateLabel: formatPercent(correctRate),
    averageAnswerLabel: formatDuration(averageAnswerMs),
    fastestAnswerLabel: fastestAnswer ? `${formatDuration(Number(fastestAnswer.answerMs))}` : '—',
    questionRows,
    teamRows,
    difficultyRows,
    fastestByDifficulty,
  };
}

function flattenAnswers(answerMap: Record<string, Record<string, AnswerRecord>>, teams: TeamPublic[]): FlatAnswer[] {
  const teamNames = new Map(teams.map((team) => [team.id, team.name]));
  const rows: FlatAnswer[] = [];
  Object.entries(answerMap).forEach(([teamId, teamAnswers]) => {
    Object.values(teamAnswers).forEach((answer) => {
      if (!answer.questionId) return;
      rows.push({
        ...answer,
        teamId,
        teamName: teamNames.get(teamId) ?? teamId.slice(0, 8),
        questionId: answer.questionId,
        difficulty: difficultyFromQuestionId(answer.questionId),
      });
    });
  });
  return rows;
}

function buildQuestionRows(answers: FlatAnswer[]) {
  const groups = groupBy(answers, (answer) => answer.questionId);
  const rows = [...groups.entries()].map(([questionId, rowsForQuestion]) => {
    const timed = rowsForQuestion.filter((answer) => typeof answer.answerMs === 'number' && answer.answerMs > 0);
    const averageMs = average(timed.map((answer) => Number(answer.answerMs)));
    return {
      questionId,
      count: rowsForQuestion.length,
      correctRate: rowsForQuestion.length ? rowsForQuestion.filter((answer) => answer.correct).length / rowsForQuestion.length : 0,
      averageMs,
      barPercent: 0,
    };
  });
  const maxAverage = Math.max(1, ...rows.map((row) => row.averageMs));
  return rows
    .map((row) => ({ ...row, barPercent: row.averageMs ? (row.averageMs / maxAverage) * 100 : 4 }))
    .sort((a, b) => b.count - a.count || b.averageMs - a.averageMs || a.questionId.localeCompare(b.questionId));
}

function buildTeamRows(answers: FlatAnswer[], teams: TeamPublic[]) {
  const groups = groupBy(answers, (answer) => answer.teamId);
  const teamScores = new Map(teams.map((team) => [team.id, team.score]));
  return [...groups.entries()]
    .map(([teamId, rows]) => {
      const timed = rows.filter((answer) => typeof answer.answerMs === 'number' && answer.answerMs > 0);
      return {
        teamId,
        teamName: rows[0]?.teamName ?? teamId.slice(0, 8),
        count: rows.length,
        correct: rows.filter((answer) => answer.correct).length,
        averageMs: average(timed.map((answer) => Number(answer.answerMs))),
        score: teamScores.get(teamId) ?? 0,
      };
    })
    .sort((a, b) => b.score - a.score || b.correct - a.correct || a.averageMs - b.averageMs);
}

function buildDifficultyRows(answers: FlatAnswer[]) {
  const rows = (['easy', 'medium', 'hard'] as const).map((difficulty) => {
    const rowsForDifficulty = answers.filter((answer) => answer.difficulty === difficulty);
    const timed = rowsForDifficulty.filter((answer) => typeof answer.answerMs === 'number' && answer.answerMs > 0);
    return {
      difficulty,
      count: rowsForDifficulty.length,
      correctRate: rowsForDifficulty.length ? rowsForDifficulty.filter((answer) => answer.correct).length / rowsForDifficulty.length : 0,
      averageMs: average(timed.map((answer) => Number(answer.answerMs))),
    };
  });
  return rows;
}

function groupBy<T>(items: T[], keyFor: (item: T) => string) {
  const groups = new Map<string, T[]>();
  items.forEach((item) => {
    const key = keyFor(item);
    groups.set(key, [...(groups.get(key) ?? []), item]);
  });
  return groups;
}

function average(values: number[]) {
  if (!values.length) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function difficultyFromQuestionId(questionId: string): DifficultyBucket {
  if (questionId.startsWith('h')) return 'hard';
  if (questionId.startsWith('m')) return 'medium';
  return 'easy';
}

function difficultyLabel(difficulty: DifficultyBucket) {
  if (difficulty === 'hard') return 'سخت';
  if (difficulty === 'medium') return 'متوسط';
  return 'آسان';
}

function difficultyTone(difficulty: DifficultyBucket) {
  if (difficulty === 'hard') return 'bg-red-500';
  if (difficulty === 'medium') return 'bg-amber-500';
  return 'bg-emerald-500';
}

function formatDuration(value: number | null | undefined) {
  if (!value || !Number.isFinite(value)) return '—';
  return `${(value / 1000).toLocaleString('fa-IR', { maximumFractionDigits: 1 })} ثانیه`;
}

function formatPercent(value: number) {
  return `${Math.round(value * 100).toLocaleString('fa-IR')}٪`;
}

function TeamCodesPanel({
  codes,
  teams,
  busyAction,
  codeLabel,
  setCodeLabel,
  newCode,
  copiedCode,
  createTeamCode,
  codeAction,
  copyTeamCode,
}: {
  codes: TeamCode[];
  teams: TeamPublic[];
  busyAction: string;
  codeLabel: string;
  setCodeLabel: (value: string) => void;
  newCode: { code: string; teamId: string } | null;
  copiedCode: string;
  createTeamCode: () => Promise<void>;
  codeAction: (codeHash: string, nextStatus: TeamCode['status']) => Promise<void> | void;
  copyTeamCode: (code: string) => Promise<void>;
}) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
      <span className="text-[10px] font-black text-brand">کدهای ورود</span>
      <h2 className="mt-1 text-lg font-black">کد تازه برای تیم</h2>
      <p className="mt-2 text-[11px] leading-6 text-slate-500">هر کد هویت همان تیم در همین مسابقه است. کدهای جدید بعد از ساخت هم همین‌جا کامل می‌مانند و قابل کپی هستند.</p>
      <div className="mt-4 flex gap-2">
        <Input value={codeLabel} onChange={(event) => setCodeLabel(event.target.value)} placeholder="مثلاً تیم آلفا" />
        <Button size="icon" variant="dark" disabled={busyAction !== ''} onClick={() => void createTeamCode()} aria-label="ساخت کد">
          <KeyRound className="size-4" />
        </Button>
      </div>
      {newCode && (
        <div className="mt-4 rounded-xl border border-brand/25 bg-brand/8 p-3">
          <span className="text-[10px] font-bold text-brand">کدی که باید به تیم بدهید</span>
          <div className="mt-1 flex items-center justify-between gap-3">
            <b className="block font-mono text-lg tracking-widest text-ink" dir="ltr">{newCode.code}</b>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-lg bg-white px-2 py-1 text-[10px] font-black text-slate-600 ring-1 ring-brand/15 transition hover:bg-orange-50"
              onClick={() => void copyTeamCode(newCode.code)}
            >
              {copiedCode === newCode.code ? <CheckCircle2 className="size-3.5 text-emerald-600" /> : <Copy className="size-3.5" />}
              {copiedCode === newCode.code ? 'کپی شد' : 'کپی'}
            </button>
          </div>
          <small className="mt-1 block text-[10px] text-slate-400" dir="ltr">{newCode.teamId}</small>
        </div>
      )}
      <div className="mt-4 max-h-72 space-y-2 overflow-auto scrollbar-thin">
        {codes.map((code) => {
          const team = teams.find((item) => item.id === code.teamId);
          const visibleCode = code.code ?? code.codePreview;
          return (
            <div key={code.id} className="rounded-xl border border-slate-200 p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <b className="block truncate text-xs">{code.label || team?.name || 'کد بی‌نام'}</b>
                  <span className="mt-1 block font-mono text-sm font-black tracking-wider text-ink" dir="ltr">{visibleCode}</span>
                  {!code.code && <span className="mt-1 block text-[10px] text-amber-600">کد قدیمی است؛ فقط پیش‌نمایش آن ذخیره شده.</span>}
                  {code.code && (
                    <button
                      type="button"
                      className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-2 py-1 text-[10px] font-black text-slate-600 transition hover:bg-slate-200"
                      onClick={() => void copyTeamCode(code.code!)}
                    >
                      {copiedCode === code.code ? <CheckCircle2 className="size-3.5 text-emerald-600" /> : <Copy className="size-3.5" />}
                      {copiedCode === code.code ? 'کپی شد' : 'کپی کد'}
                    </button>
                  )}
                </div>
                <div className="grid min-w-28 grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    className={cn('min-h-8 px-2 text-[10px]', code.status === 'active' && 'bg-emerald-600 text-white hover:bg-emerald-600 hover:text-white')}
                    disabled={busyAction !== ''}
                    onClick={() => code.status !== 'active' && void codeAction(code.id, 'active')}
                  >
                    فعال
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className={cn('min-h-8 px-2 text-[10px]', code.status === 'revoked' && 'bg-red-600 text-white hover:bg-red-600 hover:text-white')}
                    disabled={busyAction !== ''}
                    onClick={() => code.status !== 'revoked' && void codeAction(code.id, 'revoked')}
                  >
                    غیرفعال
                  </Button>
                </div>
              </div>
              <div className="mt-2 flex items-center justify-between text-[10px] text-slate-400">
                <span>{team ? `استفاده‌شده توسط ${team.name}` : 'آماده برای تیم جدید'}</span>
                <span>{Number(code.redeemCount ?? 0).toLocaleString('fa-IR')} بار ورود</span>
              </div>
            </div>
          );
        })}
        {!codes.length && <div className="rounded-xl border border-dashed border-slate-200 p-4 text-center text-xs text-slate-400">هنوز کدی ساخته نشده است.</div>}
      </div>
    </div>
  );
}

function ArchivePanel({ archives }: { archives: ContestArchive[] }) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
      <span className="flex items-center gap-2 text-[10px] font-black text-brand"><History className="size-4" /> آرشیو مسابقات</span>
      <h2 className="mt-1 text-lg font-black">نتایج دوره‌های قبلی</h2>
      <div className="mt-4 max-h-96 space-y-3 overflow-auto scrollbar-thin">
        {archives.map((archive, index) => (
          <details key={archive.id} className="group rounded-xl border border-slate-200 p-3 open:bg-slate-50">
            <summary className="cursor-pointer list-none">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <b className="flex items-center gap-2 truncate text-xs"><Trophy className="size-4 text-amber-500" /> مسابقه {toFaNumber(archives.length - index)}</b>
                  <span className="mt-1 block text-[10px] text-slate-400">{formatDateTime(archive.finishedAtMs)} · {archive.teamCount.toLocaleString('fa-IR')} تیم</span>
                </div>
                <div className="text-left">
                  <b className="block truncate text-xs">{archive.winnerName ?? 'بدون برنده'}</b>
                  <span className="font-mono text-[10px] text-slate-400">{archive.totalScore.toLocaleString('fa-IR')} امتیاز ترکیبی کل</span>
                </div>
              </div>
            </summary>
            <div className="mt-4 space-y-3 border-t border-slate-200 pt-3">
              <div>
                <span className="mb-2 flex items-center gap-2 text-[10px] font-black text-slate-500"><ListChecks className="size-3.5" /> رتبه‌ها</span>
                <div className="space-y-1.5">
                  {archive.teams.map((team) => (
                    <div key={team.id} className="flex items-center justify-between rounded-lg bg-white px-2 py-2 text-[11px]">
                      <span>{team.finalRank?.toLocaleString('fa-IR') ?? '—'} · {team.name}</span>
                      <span className="font-mono">{team.score.toLocaleString('fa-IR')} امتیاز ترکیبی / {team.correctCount.toLocaleString('fa-IR')} درست</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <span className="mb-2 block text-[10px] font-black text-slate-500">لاگ مدیریتی</span>
                <div className="max-h-32 space-y-1 overflow-auto rounded-lg bg-slate-950 p-2 text-[10px] text-slate-200 scrollbar-thin" dir="ltr">
                  {archive.auditLogs.map((log) => (
                    <div key={log.id}>{new Date(log.createdAtMs).toLocaleTimeString('fa-IR')} · {log.action}{log.teamId ? ` · ${log.teamId.slice(0, 8)}` : ''}</div>
                  ))}
                  {!archive.auditLogs.length && <div dir="rtl">لاگی ثبت نشده است</div>}
                </div>
              </div>
            </div>
          </details>
        ))}
        {!archives.length && <div className="rounded-xl border border-dashed border-slate-200 p-4 text-center text-xs text-slate-400">هنوز مسابقه‌ای آرشیو نشده است.</div>}
      </div>
    </div>
  );
}

function ScorePanel({
  teams,
  selectedTeam,
  setSelectedTeam,
  delta,
  setDelta,
  reason,
  setReason,
  busyAction,
  adjustScore,
}: {
  teams: TeamPublic[];
  selectedTeam: string;
  setSelectedTeam: (value: string) => void;
  delta: string;
  setDelta: (value: string) => void;
  reason: string;
  setReason: (value: string) => void;
  busyAction: string;
  adjustScore: () => Promise<void>;
}) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
      <span className="text-[10px] font-black text-brand">داوری امتیاز</span>
      <h2 className="mt-1 text-lg font-black">تنظیم دستی امتیاز</h2>
      <div className="mt-4 space-y-3">
        <select className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-xs outline-none focus:border-brand" value={selectedTeam} onChange={(event) => setSelectedTeam(event.target.value)}>
          <option value="">انتخاب تیم</option>
          {teams.map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
        </select>
        <Input type="number" value={delta} onChange={(event) => setDelta(event.target.value)} placeholder="مقدار مثبت یا منفی" />
        <Input value={reason} onChange={(event) => setReason(event.target.value)} placeholder="دلیل تغییر امتیاز (اجباری)" />
        <Button className="w-full" variant="dark" size="sm" disabled={!selectedTeam || !delta || reason.trim().length < 5 || busyAction !== ''} onClick={() => void adjustScore()}>
          ثبت با لاگ داوری
        </Button>
      </div>
    </div>
  );
}

function HeroMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 border-r border-white/10 pr-3 first:border-r-0 first:pr-0">
      <span className="text-[10px] font-black text-white/35">{label}</span>
      <strong className="mt-1 block truncate text-lg font-black">{value}</strong>
    </div>
  );
}

function Metric({ icon, label, value, accent = false }: { icon: React.ReactNode; label: string; value: string; accent?: boolean }) {
  return <div className="flex items-center gap-4"><span className={cn('grid size-11 place-items-center rounded-xl', accent ? 'bg-brand text-white' : 'bg-white text-slate-500 ring-1 ring-slate-200')}>{icon}</span><div><span className="text-[10px] font-bold text-slate-400">{label}</span><strong className="block text-xl font-black">{value}</strong></div></div>;
}

function activeFlowStep(event: ArenaEvent) {
  if (event.status === 'registration' && event.admissionOpen) return 1;
  if (event.status === 'registration') return 2;
  if (event.status === 'countdown' || event.status === 'running' || event.status === 'paused') return 3;
  if (event.status === 'finished') return 4;
  return 5;
}

function formatDateTime(value: number) {
  return new Date(value).toLocaleString('fa-IR', { dateStyle: 'short', timeStyle: 'short' });
}

function toFaNumber(value: number) {
  return value.toLocaleString('fa-IR');
}
